import BaseMap from 'components/BaseMap/BaseMap';
import BaseSelect from 'components/Selects/BaseSelect/BaseSelect';
import { findSelectOption } from 'components/Selects/BaseSelect/utils/baseSelect.utils';
import { DEFAULT_LOCATION_COORDS } from 'config/defaultLocation.config.js';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from 'react-bootstrap';
import { IconContext } from 'react-icons';
import {
  IoAccessibility,
  IoInformationCircle,
  IoMapOutline,
  IoSaveOutline,
  IoStatsChartOutline,
} from 'react-icons/io5';
import { useDispatch, useSelector } from 'react-redux';
import { getUserProfileInfo, updateUserProfileOptions } from 'services/user/user.service';
import { NAVIGATION_STYLES } from 'store/config/displayOptions.config';
import {
  setAccessibilityColorMode,
  setChartStyle,
  setMapStyle,
  setNavigationStyle,
} from 'store/slices/DisplayOptions/displayOptionsSlice';
import {
  selectAccessibilityOptions,
  selectChartOptions,
  selectMapOptions,
  selectNavigationStyle,
} from 'store/selectors/DisplayOptions/displayOptionsSelectors';
import {
  CHART_STYLE_OPTIONS,
  getChartContainerStyles,
  normalizeChartOptions,
} from 'utils/charts/chartStyle.utils';
import { ACCESSIBILITY_COLOR_MODES } from 'config/accessibilityTheme.config';
import { DEFAULT_DISPLAY_THEME } from 'config/theme.config';
import { MAP_STYLE_OPTIONS, normalizeMapOptions } from 'utils/maps/mapTile.utils';
import {
  mergeProfileDisplayOptions,
  normalizeDisplayOptionsForProfile,
} from 'utils/preferences/userOptions.utils';

const NAVIGATION_STYLE_OPTIONS = [
  { label: 'Merlin Slate', value: NAVIGATION_STYLES.MERLIN },
  { label: 'Classic', value: NAVIGATION_STYLES.CLASSIC },
];

const SettingRow = ({ label, children }) => (
  <div className="d-flex flex-column flex-sm-row gap-2 gap-sm-3 mb-3 w-100 justify-content-between align-items-start align-items-sm-center">
    <div className="flex-shrink-0" style={{ width: 134 }}>
      {label}
    </div>
    {children}
  </div>
);

const SettingsCard = ({ icon, title, children }) => (
  <div
    className="d-flex flex-column align-items-start rounded-2 p-3 p-md-4 h-100 w-100"
    style={{
      backgroundColor: 'var(--color-primary-highlight)',
      color: 'var(--color-primary-highlight-foreground)',
    }}
  >
    <div
      className="rounded-3 px-3 py-2 mb-3 fw-bold d-flex align-items-center gap-2"
      style={{
        backgroundColor: 'var(--color-primary)',
        color: 'var(--color-primary-foreground)',
      }}
    >
      {icon}
      {title}
    </div>
    {children}
  </div>
);

const SELECT_STYLES = {
  container: (base) => ({ ...base, maxWidth: 220 }),
};

const ACCESSIBILITY_STYLE_VALUES = new Set([
  ACCESSIBILITY_COLOR_MODES.MONOCHROME,
  ACCESSIBILITY_COLOR_MODES.ENHANCED_CONTRAST,
  ACCESSIBILITY_COLOR_MODES.HIGH_CONTRAST,
]);

const ACCESSIBILITY_COLOR_MODE_OPTIONS = [
  { value: ACCESSIBILITY_COLOR_MODES.DEFAULT, label: 'Default' },
  { value: ACCESSIBILITY_COLOR_MODES.MONOCHROME, label: 'Monochrome' },
  { value: ACCESSIBILITY_COLOR_MODES.ENHANCED_CONTRAST, label: 'Enhanced Contrast' },
  { value: ACCESSIBILITY_COLOR_MODES.HIGH_CONTRAST, label: 'High Contrast' },
];

const MAP_SELECT_OPTIONS = MAP_STYLE_OPTIONS.filter(
  (option) => !ACCESSIBILITY_STYLE_VALUES.has(option.value),
);

const CHART_SELECT_OPTIONS = CHART_STYLE_OPTIONS.filter(
  (option) => !ACCESSIBILITY_STYLE_VALUES.has(option.value),
);

const THEME_STYLE_OPTIONS = MAP_SELECT_OPTIONS;

const CUSTOM_THEME_STYLE_OPTION = {
  value: 'custom',
  label: 'Custom',
};

const MAP_PREVIEW_STYLE = {
  height: 'clamp(110px, 15vh, 150px)',
};

const CHART_PREVIEW_STYLE = {
  height: 'clamp(105px, 14vh, 130px)',
  minHeight: 105,
};

const MapPreview = ({ mapOptions }) => {
  const normalizedOptions = useMemo(() => normalizeMapOptions(mapOptions), [mapOptions]);

  return (
    <div className="w-100 mb-3">
      <div className="d-flex align-items-center gap-2 mb-2 fw-semibold">
        <IoMapOutline />
        <span>Map Preview</span>
      </div>
      <div className="overflow-hidden rounded-2 border" style={MAP_PREVIEW_STYLE}>
        <BaseMap
          key={normalizedOptions.mapStyle}
          mapCenter={DEFAULT_LOCATION_COORDS}
          zoom={11}
          minZoom={3}
          maxZoom={18}
          scrollZoom={false}
          disableMapControls
          disableMapReset
          mapOptions={normalizedOptions}
        ></BaseMap>
      </div>
    </div>
  );
};

const ChartPreview = ({ chartOptions }) => {
  const normalizedOptions = useMemo(() => normalizeChartOptions(chartOptions), [chartOptions]);
  const chartTheme = getChartContainerStyles(normalizedOptions);

  return (
    <div className="w-100 mb-3">
      <div className="d-flex align-items-center gap-2 mb-2 fw-semibold">
        <IoStatsChartOutline />
        <span>Chart Preview</span>
      </div>
      <div
        className={`overflow-hidden rounded-2 border ${chartTheme.className || ''}`}
        style={{
          ...CHART_PREVIEW_STYLE,
          backgroundColor: chartTheme.panelBackground,
          color: chartTheme.textColor,
          padding: 12,
        }}
      >
        <div className="small fw-semibold mb-2">Temperature Trend</div>
        <div
          className="position-relative rounded-2 h-75"
          style={{
            backgroundColor: chartTheme.plotBackground,
            border: '1px solid rgba(128, 128, 128, 0.24)',
          }}
        >
          {[20, 45, 70].map((top) => (
            <div
              key={top}
              className="position-absolute start-0 end-0"
              style={{
                top: `${top}%`,
                borderTop: '1px solid currentColor',
                opacity: 0.18,
              }}
            />
          ))}
          <svg viewBox="0 0 260 80" className="position-absolute top-0 start-0 w-100 h-100">
            <polyline
              points="8,60 55,44 105,52 154,25 205,34 252,14"
              fill="none"
              stroke="#63a1e0"
              strokeWidth="4"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <polyline
              points="8,69 55,57 105,62 154,43 205,47 252,31"
              fill="none"
              stroke="#f59e0b"
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
      </div>
    </div>
  );
};

/**
 * The classic Accessibility tab — the page as it was before the Merlin Slate restyle (0c1fb517e).
 * Shown when the person picks Classic navigation in the display settings.
 */
export default function ClassicAccessibilitySettings() {
  const dispatch = useDispatch();
  const mapOptions = useSelector(selectMapOptions);
  const chartOptions = useSelector(selectChartOptions);
  const accessibilityOptions = useSelector(selectAccessibilityOptions);
  const navigationStyle = useSelector(selectNavigationStyle);
  const [saveStatus, setSaveStatus] = useState('idle');
  const normalizedMapOptions = normalizeMapOptions(mapOptions);
  const normalizedChartOptions = normalizeChartOptions(chartOptions);
  const selectedThemeStyle =
    normalizedMapOptions.mapStyle === normalizedChartOptions.chartStyle
      ? normalizedMapOptions.mapStyle
      : CUSTOM_THEME_STYLE_OPTION.value;
  const themeStyleOptions =
    selectedThemeStyle === CUSTOM_THEME_STYLE_OPTION.value
      ? [CUSTOM_THEME_STYLE_OPTION, ...THEME_STYLE_OPTIONS]
      : THEME_STYLE_OPTIONS;
  const normalizedDisplayOptions = useMemo(
    () =>
      normalizeDisplayOptionsForProfile({
        displayTheme: DEFAULT_DISPLAY_THEME,
        mapOptions,
        chartOptions,
        accessibilityOptions,
      }),
    [accessibilityOptions, chartOptions, mapOptions],
  );
  const serializedDisplayOptions = useMemo(
    () => JSON.stringify(normalizedDisplayOptions),
    [normalizedDisplayOptions],
  );
  const lastSavedDisplayOptionsRef = useRef(serializedDisplayOptions);
  const colorMode = accessibilityOptions.colorMode;
  const isSaving = saveStatus === 'saving';

  useEffect(() => {
    if (saveStatus === 'saved' && lastSavedDisplayOptionsRef.current !== serializedDisplayOptions) {
      setSaveStatus('idle');
    }
  }, [saveStatus, serializedDisplayOptions]);

  const handleThemeStyleChange = (style) => {
    dispatch(setMapStyle(style));
    dispatch(setChartStyle(style));
  };

  const handleSaveSettings = async () => {
    setSaveStatus('saving');

    try {
      const response = await getUserProfileInfo();
      const profileResult = response?.data?.result;

      if (!profileResult || typeof profileResult !== 'object') {
        setSaveStatus('error');
        return;
      }

      const currentProfileOptions =
        profileResult.options && typeof profileResult.options === 'object'
          ? profileResult.options
          : {};

      const nextProfileOptions = mergeProfileDisplayOptions(
        currentProfileOptions,
        normalizedDisplayOptions,
      );
      const saveResponse = await updateUserProfileOptions(nextProfileOptions);

      if (saveResponse?.status >= 200 && saveResponse?.status < 300) {
        lastSavedDisplayOptionsRef.current = serializedDisplayOptions;
        setSaveStatus('saved');
      } else {
        setSaveStatus('error');
      }
    } catch (error) {
      setSaveStatus('error');
    }
  };

  return (
    <div className="w-100 h-100 mt-3 pt-3 px-0">
      <div className="row g-3 g-xl-4 align-items-start">
        <div className="col-12 col-lg-4 col-xxl-3">
          <SettingsCard icon={<IoAccessibility />} title="Accessibility">
            <SettingRow label="Theme">
              <BaseSelect
                styles={SELECT_STYLES}
                selectProps={{
                  'aria-label': 'Theme',
                  options: themeStyleOptions,
                  value: findSelectOption(themeStyleOptions, selectedThemeStyle),
                  isOptionDisabled: (option) => option.value === CUSTOM_THEME_STYLE_OPTION.value,
                  onChange: (option) => handleThemeStyleChange(option.value),
                }}
              />
            </SettingRow>

            {/* Applies at once and stays on this device; it is not part of Save Settings. */}
            <SettingRow label="Navigation">
              <BaseSelect
                styles={SELECT_STYLES}
                selectProps={{
                  'aria-label': 'Navigation',
                  options: NAVIGATION_STYLE_OPTIONS,
                  value: findSelectOption(NAVIGATION_STYLE_OPTIONS, navigationStyle),
                  onChange: (option) => dispatch(setNavigationStyle(option.value)),
                }}
              />
            </SettingRow>

            <div className="fw-bold mb-2">Accessibility</div>
            <SettingRow label="Color Mode">
              <BaseSelect
                styles={SELECT_STYLES}
                selectProps={{
                  'aria-label': 'Color Accessibility Mode',
                  options: ACCESSIBILITY_COLOR_MODE_OPTIONS,
                  value: findSelectOption(ACCESSIBILITY_COLOR_MODE_OPTIONS, colorMode),
                  onChange: (option) => dispatch(setAccessibilityColorMode(option.value)),
                }}
              />
            </SettingRow>
            <div className="d-flex flex-column align-items-stretch gap-2 mt-2 w-100">
              <Button
                variant="secondary"
                className="d-flex align-items-center justify-content-center gap-2"
                onClick={handleSaveSettings}
                disabled={isSaving}
              >
                <IoSaveOutline />
                {isSaving ? 'Saving...' : 'Save Settings'}
              </Button>
              {saveStatus === 'saved' && <div className="small text-center">Settings saved</div>}
              {saveStatus === 'error' && (
                <div className="small text-center">Unable to save settings</div>
              )}
            </div>
          </SettingsCard>
        </div>

        <div className="col-12 col-lg-8 col-xxl-7">
          <SettingsCard icon={<IoMapOutline />} title="Site Settings">
            <div className="d-flex flex-column flex-sm-row align-items-center mb-3">
              <IconContext.Provider value={{ size: 30 }}>
                <IoInformationCircle />
              </IconContext.Provider>
              <p className="fs-6 text-center m-0">These settings apply per user on this browser</p>
            </div>

            <div className="row g-3 w-100">
              <div className="col-12 col-xl-6">
                <div className="fw-bold mb-2">Map Settings</div>
                <SettingRow label="Map Style">
                  <BaseSelect
                    styles={SELECT_STYLES}
                    selectProps={{
                      'aria-label': 'Map Style',
                      options: MAP_SELECT_OPTIONS,
                      value: findSelectOption(MAP_SELECT_OPTIONS, normalizedMapOptions.mapStyle),
                      onChange: (option) => dispatch(setMapStyle(option.value)),
                    }}
                  />
                </SettingRow>
                <MapPreview mapOptions={normalizedMapOptions} />
              </div>

              <div className="col-12 col-xl-6">
                <div className="fw-bold mb-2">Chart Settings</div>
                <SettingRow label="Chart Style">
                  <BaseSelect
                    styles={SELECT_STYLES}
                    selectProps={{
                      'aria-label': 'Chart Style',
                      options: CHART_SELECT_OPTIONS,
                      value: findSelectOption(
                        CHART_SELECT_OPTIONS,
                        normalizedChartOptions.chartStyle,
                      ),
                      onChange: (option) => dispatch(setChartStyle(option.value)),
                    }}
                  />
                </SettingRow>
                <ChartPreview chartOptions={normalizedChartOptions} />
              </div>
            </div>
          </SettingsCard>
        </div>
      </div>
    </div>
  );
}
